import { useState, type FormEvent } from 'react';
import type { ProcessAttendanceResult } from '@mervonn/shared-types';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { Modal } from '../../components/ui/Dialog';
import { ErrorNote, InlineStat } from '../../components/ui/misc';
import { ApiError } from '../../lib/api/errors';
import { useProcessAttendance } from './hooks';

function daysAgo(n: number): string {
  return new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);
}

export function ProcessAttendanceDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const process = useProcessAttendance();
  const [from, setFrom] = useState(daysAgo(7));
  const [to, setTo] = useState(daysAgo(0));
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProcessAttendanceResult | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (to < from) {
      setError('The end date must be on or after the start date.');
      return;
    }
    try {
      const r = await process.mutateAsync({ from, to });
      setResult(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Processing failed. Try again.');
    }
  }

  function close() {
    onOpenChange(false);
    setResult(null);
    setError(null);
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => (o ? onOpenChange(true) : close())}
      title="Process attendance"
      description="Attendance is processed automatically after each punch. Use this to rebuild past days, for example after a correction. Safe to re-run: it converges to the same result."
      footer={
        result ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" type="button" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" form="process-form" loading={process.isPending}>
              Run
            </Button>
          </>
        )
      }
    >
      {result ? (
        <dl className="flex flex-wrap gap-x-8 gap-y-3">
          <InlineStat label="Records" value={result.processed_records} />
          <InlineStat label="Exceptions" value={result.exceptions_created} />
          <InlineStat label="Unresolved punches" value={result.unresolved_events} />
        </dl>
      ) : (
        <form id="process-form" onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
          {error && <div className="sm:col-span-2"><ErrorNote>{error}</ErrorNote></div>}
          <Field label="From" type="date" required value={from} onChange={(e) => setFrom(e.target.value)} />
          <Field label="To" type="date" required value={to} onChange={(e) => setTo(e.target.value)} />
        </form>
      )}
    </Modal>
  );
}
